/**
 * Terminal keyboard-hold policy constants shared by node, Command, and tests.
 *
 * Must stay aligned with supabase migration
 * `20260923053500_terminal_owner_hold_idle_release.sql`.
 */

/** Completed owner input keeps a short grace window before automation may type. */
export const OWNER_KEYSTROKE_HOLD_SECONDS = 12;

/**
 * A partial owner line is a truthful active-input signal, not ordinary idle time.
 * Keep it protected long enough for a human pause, with a bounded crash/disconnect
 * escape hatch. Enter, Ctrl+C, or Ctrl+U returns to the short grace window.
 */
export const OWNER_PENDING_INPUT_HOLD_SECONDS = 600;

/** Machine write renewal when a controller already holds the keyboard. */
export const CONTROLLER_WRITE_HOLD_SECONDS = 120;

export type TerminalInputHolderKind =
  | 'owner_terminal'
  | 'owner_composer'
  | 'command_ai'
  | 'worker_automation'
  | 'system'
  | string;

/** True when the holder is a person source that must always win over automation. */
export function terminalHolderIsPerson(holder: string | null | undefined): boolean {
  return holder === 'owner_terminal' || holder === 'owner_composer';
}

/**
 * Whether automation may write right now.
 *
 * `held` is the authoritative flag from node_terminal_input_hold / feed sizes.
 * A leftover holder name after idle expiry must not block once held is false.
 */
export function automationMayWrite(input: {
  readonly held: boolean;
  readonly holder?: string | null;
}): boolean {
  return input.held !== true;
}

/**
 * Owner keyboard priority is active only while the authoritative hold is true
 * and the holder is a person source.
 */
export function ownerKeyboardPriorityActive(input: {
  readonly held: boolean;
  readonly holder?: string | null;
}): boolean {
  return input.held === true && terminalHolderIsPerson(input.holder);
}

/* eslint-disable no-control-regex */
/** OSC / DCS / SOS / PM / APC strings, terminated by BEL or ST. A person never types these. */
const TERMINAL_STRING = /\u001b[\]P^_X][^\u0007\u001b]*(?:\u0007|\u001b\\)/g;
/** CSI sequences, plus SS3 cursor/function keys (application cursor mode). */
const TERMINAL_KEY_SEQUENCE = /\u001b\[[0-?]*[ -/]*[@-~]|\u001bO[@-~]/g;
/**
 * Replies the terminal emulator itself sends when the application queries it:
 * OSC/DCS reports (colours, XTVERSION, DECRQSS), cursor position (CPR/DECXCPR),
 * device attributes, device status, focus in/out, mode (DECRPM), window and
 * keyboard-flag reports. Keys a person presses (arrows, function keys, Alt, paste
 * brackets, control bytes) are never in this set.
 */
const TERMINAL_PROTOCOL_REPLIES = new RegExp(
  '^(?:'
  + '\\u001b[\\]P][^\\u0007\\u001b]*(?:\\u0007|\\u001b\\\\)'
  + '|\\u001b\\[\\??\\d+;\\d+R'
  + '|\\u001b\\[[?>][\\d;]*c'
  + '|\\u001b\\[\\??[\\d;]*n'
  + '|\\u001b\\[[IO]'
  + '|\\u001b\\[\\??\\d+;\\d+\\$y'
  + '|\\u001b\\[\\d+(?:;\\d+)*t'
  + '|\\u001b\\[\\?\\d*u'
  + ')+$',
);
/* eslint-enable no-control-regex */

/**
 * True when raw terminal input consists only of automatic terminal-emulator
 * protocol replies. Such bytes must still reach the PTY (the application asked
 * for them) but are not a person at the keyboard, so they must not create,
 * extend, or take an owner input hold.
 */
export function terminalProtocolReplyOnly(data: string): boolean {
  return data !== '' && TERMINAL_PROTOCOL_REPLIES.test(data);
}

/**
 * Track whether raw terminal input leaves an unfinished owner line in the PTY.
 * Cursor/function-key escape sequences and terminal protocol replies do not
 * create a line. Backspace remains conservative because raw input cannot prove
 * that the shell buffer is empty.
 */
export function ownerInputLineOpen(previous: boolean, data: string): boolean {
  let open = previous;
  const withoutCsi = data.replace(TERMINAL_STRING, '').replace(TERMINAL_KEY_SEQUENCE, '');
  for (const character of withoutCsi) {
    const code = character.charCodeAt(0);
    if (character === '\r' || character === '\n' || code === 3 || code === 21) {
      open = false;
    } else if (code >= 32 && code !== 127) {
      open = true;
    }
  }
  return open;
}
